import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import styles from "./PhotoTab.module.scss";
import classNames from "classnames/bind";
import { CloseIcon, EditIcon, PlusIcon, SearchIcon, TrashIcon } from "~/assets/icons";
import { Calendar1Icon } from "lucide-react";
import { useAppDispatch, useAppSelector } from "~/redux/hooks";
import { useEffect, useRef, useState } from "react";
import { handleDeletePhoto, handleGetPhoto, handleGetPhotoDetail, handleUpdatePhoto, handleUploadPhotos } from "~/thunks/job/jobThunk";
import { notify } from "~/utils/helper";
import { Box, IconButton, Modal } from "@mui/material";
import UploadPhoto from "./UploadPhoto";
import UploadFileModal from "~/pages/admin/crm/details/tabs/document/add-new-lead/UploadFile";
import { useParams } from "react-router-dom";
import ModalAlertDelete from "~/components/common/modal-alert-delete/ModalAlertDelete";
import { useTranslation } from "react-i18next";
import PhotoImage from "./PhotoImage";

const cx = classNames.bind(styles);

// Encode URL to handle spaces and special characters
const encodeImageUrl = (url: string): string => {
  if (!url) return '';

  // Simply replace specific problematic characters
  // This is more reliable than full URL encoding
  return url
    .replace(/ /g, '%20')      // Replace spaces
    .replace(/#/g, '%23')      // Replace hash
    .replace(/\(/g, '%28')     // Replace (
    .replace(/\)/g, '%29')     // Replace )
    .replace(/\[/g, '%5B')     // Replace [
    .replace(/\]/g, '%5D');    // Replace ]
};

interface photoType {
  id: string,
  url: string,
  title:string,
  description: string,
  createdBy : {firstName: string, lastName: string},
  updateTime: Date,
}

const useInfiniteScroll = (
  onLoadMore: () => void,
  hasMore: boolean,
  loading: boolean
) => {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasMore || loading) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onLoadMore();
        }
      },
      { rootMargin: "100px" } // load sớm 1 chút
    );

    if (ref.current) observer.observe(ref.current);

    return () => observer.disconnect();
  }, [hasMore, loading, onLoadMore]);

  return ref;
};

const PhotoTab = () => {
  const { id } = useParams();

  const { t } = useTranslation(['admin/job', 'admin/common']);
  
  
  const dispatch = useAppDispatch();

  const [photos, setPhotos] = useState<photoType[]>([])
  const [filterName, setFilterName] = useState('');

  const [error, setError] = useState({
    description: "",
    file: "",
  });

  const {timeUpdatePhoto, isUpdatingJobDetails } = useAppSelector(
    (state) => state.jobDetails
  );

  const [isOpendUpload, setOpendUpload] = useState(false);

  const [isOpendModalDelete, setOpendModalDelete] = useState(false);
  const [idPhotoSelect, setIdPhotoSelect] = useState<string | null>(null);
  const [photoSelectDetail, setPhotoSelectDetail] = useState<photoType | null>(null);

  const [curentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const [previewImage, setPreviewImage] = useState('');
  const [isOpendModalPreview, setOpendModalPreview] = useState(false);

  const getPhoto = async (page:number = 1, search: string = filterName) => {
    if(!id) return;

    setLoading(true);

    const params: { page: number; limit: number; textSearch?: string } = {
      page: page,
      limit: 20,
    };
    if (search.trim()) {
      params.textSearch = search.trim();
    }

    await dispatch(
      handleGetPhoto({
        id: id,
        params,
      })
    ).unwrap()
    .then((res: any) => {
      let _photo: photoType[] =[];
      if(res.status == 'success'){
        res.data.responses.map((fileInfo:any) => {
          _photo.push({
            id: fileInfo.id,
            url: fileInfo.url,
            title: fileInfo.title,
            description: fileInfo.description,
            createdBy : fileInfo.createdBy ? fileInfo.createdBy : {firstName: '', lastName: ''},
            updateTime: new Date(fileInfo.createdAt),
          })
        })

        setPhotos((prev) => (page > 1 ? [...prev, ..._photo] : _photo));

        let curPage = parseInt(res.data.pagination.page);
        setCurrentPage(curPage)
        setHasMore(curPage < res.data.pagination.totalPage );
      }
    })
    .catch((error) => {
    });;

    setLoading(false);
  }

  const loadMore = async () => {
    if (loading || !hasMore) return;
    await getPhoto(curentPage + 1)
  };
  const bottomRef = useInfiniteScroll(loadMore, hasMore, loading);

  const onSavePhoto = (isCreate:boolean, data:any) => {
    if(isCreate){
      handleUpload(data);
    }else{
      handleEditPhoto(data)
    }
  }

  const handleUpload = async (data: any) => {
    if(!id) return;
    if(isUpdatingJobDetails) return;

    const files: File[] = Array.isArray(data.files) && data.files.length > 0
      ? data.files
      : data.file
        ? [data.file]
        : [];

    if (files.length === 0) {
      notify("error", "Please select at least one file.");
      return;
    }

    setLoading(true);
    try {
      for (const file of files) {
        await dispatch(
          handleUploadPhotos({
            id: id,
            param: {
              file,
              description: data.description,
              title: data.title,
            },
          })
        ).unwrap();
      }
      notify(
        "success",
        files.length > 1
          ? `${files.length} photos uploaded successfully!`
          : "Upload successful!"
      );
      setOpendUpload(false);
      await getPhoto();
    } catch (error: any) {
      notify(
        "error",
        error?.response?.data?.message ||
          error?.message ||
          "Failed to upload photo"
      );
      setLoading(false);
    }
  }

  const handleEditPhoto = async (data: any) => {
    if(!id || !photoSelectDetail) return;

    setLoading(true);
    try {
      let param = {
        file:data.file,
        description: data.description,
        title:data.title,
      }
      await dispatch(
        handleUpdatePhoto({ 
          idJob: id,
          idPhoto: photoSelectDetail.id,
          param: param })
      ).unwrap();
      notify("success", "Upload successful!");
      setOpendUpload(false);
      getPhoto();
      // dispatch(crmActions.changeNeedRefreshData(true));
    } catch (error: any) {
      notify(
        "error",
        error?.response?.data?.message ||
          error?.message ||
          "Failed to upload photo"
      );
    }
  }

  const dateToHour = (date:Date) => {
    const pad = (n: number) => String(n).padStart(2, "0");

    const hours24 = date.getHours();
    const hours12 = hours24 % 12 || 12;
    const minutes = pad(date.getMinutes());
    const ampm = hours24 >= 12 ? "PM" : "AM";

    return `${pad(hours12)}:${minutes} ${ampm}`;
  }

  const dateToDay = (date:Date) => {
    const pad = (n: number) => String(n).padStart(2, "0");
    const time = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    return time;
  }

  const convertDateLocal = (date:string) => {
    const result = date.replace(/Z$/, "");
    return result;
  }

  const onDeletePhoto = async () => {
    if(!idPhotoSelect || !id) return;

    try {
      await dispatch(
        handleDeletePhoto({ idJob: id, idPhoto: idPhotoSelect })
      ).unwrap();
      notify("success", "Delete successful!");

      setIdPhotoSelect(null);
      setOpendModalDelete(false);
      getPhoto();

    } catch (error) {
      
    }

  }

  const showDetail = async(idPhoto:string) => {
    if(!id || !idPhoto) return;

    try {
      await dispatch(handleGetPhotoDetail({
        idJob: id, 
        idPhoto: idPhoto
      })).unwrap()
      .then((res: any) => {
        if(res.status == 'success'){
          setPhotoSelectDetail({
            id: res.data.id,
            url: res.data.url,
            title: res.data.title,
            description: res.data.description,
            createdBy : res.data.createdBy ? res.data.createdBy : {firstName: '', lastName: ''},
            updateTime: new Date(convertDateLocal(res.data.createdAt) ),
          });
          setOpendUpload(true);

        }
      })
    } catch (error) {
      
    }
  }

  const showImage = async(url:string) => {

  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      getPhoto(1, filterName);
    }, filterName ? 300 : 0);
    return () => clearTimeout(timer);
  }, [filterName, timeUpdatePhoto])
  
  return (
    <div className={cx("photo-tab-container")}>

      <div className={cx('header')}>
        <div className={cx('title')}>
          <h2>Photos</h2>
          <h5>View and manage all project photos and documentation</h5>
        </div>

        <div className={cx('search')}>
          <div className={cx("search-input-box")}>
            <SearchIcon className={cx("search-icon")} />
            <input
              type="text"
              placeholder="Search"
              className={cx("search-input")}
              value={filterName}
              onChange={(e) => {setFilterName(e.target.value)}}
            />
          </div>
          <PrimaryButton
              title="Upload Photos"
              icon={<PlusIcon className={cx("create-icon")}  fill="#fff"/>}
              iconPosition="left"
              onClick={() => {
                setOpendUpload(true);
              }}
              className={cx("create-button")}
            />
        </div>
      </div>

      <div className={cx('photos-scroll')}>
        <div className={cx('photos')}>
          {
            photos
              .map((photo) => (
              <div className={cx('photo-item')} key={photo.id}>
                <div className={cx('photo-preview')}>
                  <PhotoImage url={photo.url} />
                  <div className={cx('photo-preview-icon')} onClick={()=>{
                    setPreviewImage(photo.url); 
                    setOpendModalPreview(true);
                  }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 8C10 8.53043 9.78929 9.03914 9.41421 9.41421C9.03914 9.78929 8.53043 10 8 10C7.46957 10 6.96086 9.78929 6.58579 9.41421C6.21071 9.03914 6 8.53043 6 8C6 7.46957 6.21071 6.96086 6.58579 6.58579C6.96086 6.21071 7.46957 6 8 6C8.53043 6 9.03914 6.21071 9.41421 6.58579C9.78929 6.96086 10 7.46957 10 8Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M1.63867 7.99967C2.48801 5.29501 5.01534 3.33301 8.00001 3.33301C10.9853 3.33301 13.512 5.29501 14.3613 7.99967C13.512 10.7043 10.9853 12.6663 8.00001 12.6663C5.01534 12.6663 2.48801 10.7043 1.63867 7.99967Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                </div>
                <div className={cx('photo-info')}>
            <p className={cx('photo-name')}>{photo.title}</p>
            <div className={cx("photo-user")}>
              <p>{photo.createdBy.firstName} {photo.createdBy.lastName}</p>
              <div className={cx('dot')}></div>
              <p>{dateToHour(photo.updateTime)}</p>
            </div>
            <div className="flex items-center justify-between">
              <div className={cx('time-update')}>
                <Calendar1Icon stroke="#FFFFFF"/>
                <h5>{dateToDay(photo.updateTime)}</h5>
              </div>
              <div className={cx('photo-action')}>
                <div className={cx('icon-edit')} onClick={()=>{
                    showDetail(photo.id)
                  }}><EditIcon />
                  </div>
                <div className={cx('icon-delete')} onClick={()=>{
                  setIdPhotoSelect(photo.id);
                  setOpendModalDelete(true);
                }}>
                  <TrashIcon />
                </div>
              </div>
            </div>
                </div>
              </div>
            ))
          }
          {hasMore && <div ref={bottomRef} />}
        </div>
      </div>
      
      <UploadPhoto isOpen={isOpendUpload} 
        onCancel={()=> {
          setOpendUpload(false);
        }}
        onSave={onSavePhoto}
        photo={photoSelectDetail}
      />

      <ModalAlertDelete
        open={isOpendModalDelete}
        description={t('Do you want to delete this?')}
        onCancel={()=>{
          setIdPhotoSelect(null);
          setOpendModalDelete(false);
        }}
        onConfirm={onDeletePhoto}
      />

      {isOpendModalPreview &&       
        <div className={cx("overlay-preview")} onClick={()=>{
          setPreviewImage(''); 
          setOpendModalPreview(false);
        }}>
          <div
            className={cx("upload-file-modal-container")}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={cx("header")}>
              <div></div>
              <CloseIcon color="#ffffff" fill="#ffffff" onClick={()=>{
                setPreviewImage(''); 
                setOpendModalPreview(false);
              }}/>
            </div>

            <div className={cx("content")}>
              <img src={encodeImageUrl(previewImage)} />
            </div>
          </div>
        </div>
      }

    </div>
  );
};

export default PhotoTab;
